import type { FinalWordData } from '@isoterik/react-word-cloud';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CenterFlex, Flex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import useChartWordCloudItems from '@/features/(charts)/chart-renderer/ui/Single/use-chart-word-cloud-items';
import { openSignalWordCloudPreviewDialog } from '@/features/(signal)/signal-issue-detail/ui/signal-feedback-preview-dialog';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import { TranslateLanguageSelect } from '@/features/WordCloud/components/TranslateLanguageSelect';
import { WordCloud } from '@/features/WordCloud/components/WordCloud';
import { useWordCloudTranslation } from '@/features/WordCloud/hooks/useWordCloudTranslation';
import service from '@/services';

export const WordCloudArea = (props: {
  issue: CompositedSignalIssue;
  datePreset: SignalDateSelectionInput;
  filterSet: StructuredFilter;
}) => {
  const processedFilter = useMemo(() => {
    const sFilter = new SFilter(props.filterSet);
    return sFilter.get() || {};
  }, [props.filterSet]);
  const wordCloudChartPayload = useMemo(
    (): CreateChartPayload => ({
      name: 'Signal Wordcloud',
      type: CHART_TYPE.SINGLE_AXIS,
      historical: {
        frequency: null,
        value: false,
      },
      graph: CHART_GRAPH.WORD_CLOUD,
      field: {
        type: CHART_FIELD.FEEDBACK_KEYWORD,
        entire: true,
        candidates: [],
      },
      selected_field: {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50,
        items: [],
      },
      target: {
        is_compare: false,
        term: {
          type: CHART_TERM_TYPE.FIXED,
          unit: CHART_TERM_UNIT.DAY,
          start_date: processedFilter.start_date as string,
          end_date: processedFilter.end_date as string,
        },
        filter: Object.fromEntries(
          Object.entries(processedFilter).filter(
            ([key]) => key !== 'start_date' && key !== 'end_date',
          ),
        ),
      },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: {
            view_type: CHART_METRIC_VIEW_TYPE.COUNT,
          },
        },
      ],
    }),
    [processedFilter],
  );

  const { data: wordCloudTable, isFetching } = useQuery({
    queryKey: ['word-cloud', processedFilter],
    queryFn: () => service.chart.fetchTable(wordCloudChartPayload),
  });
  const data = useChartWordCloudItems({
    chart: wordCloudChartPayload,
    tableData: wordCloudTable,
  });

  const {
    isSupported: isTranslationSupported,
    targetLanguage,
    setTargetLanguage,
    translatedWords,
    isTranslating,
  } = useWordCloudTranslation({
    words: data || [],
    enabled: !isFetching && !!data,
  });

  const onWordClick = (word: FinalWordData & { originalKeyword?: string }) => {
    // Use original Korean keyword for detail lookup when available
    const keyword = word.originalKeyword || word.text;
    openSignalWordCloudPreviewDialog({
      title: `Posts for "${word.text}"`,
      keyword,
      filter: processedFilter,
      queryKey: [
        'signal',
        'detail',
        props.issue.name,
        'word-cloud',
        keyword,
        JSON.stringify(processedFilter),
      ],
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Flex j="space-between" a="center">
        <h3 className="text-2xl font-semibold">Word cloud</h3>
        {isTranslationSupported && data && data.length > 0 && (
          <TranslateLanguageSelect
            value={targetLanguage}
            onChange={setTargetLanguage}
            isLoading={isTranslating}
            disabled={isFetching}
          />
        )}
      </Flex>
      <ToggleStage
        status={!isFetching && !isTranslating}
        data={data}
        className="w-full aspect-[2/1] min-h-[400px] p-4 rounded-2xl border border-border"
        fallback={
          <CenterFlex className="h-full">
            <Spinner />
          </CenterFlex>
        }
      >
        {() => <WordCloud words={translatedWords} onWordClick={onWordClick} />}
      </ToggleStage>
    </div>
  );
};
